import React, {useEffect, useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {debounce} from 'lodash';
import {CustomDropdown, FormButton, Icon} from '@social-native/snclient-ui';
import CreatorFilterItem from './creator_filter_item';
import './creator_filter.scss';

/**
 * CreatorFilter component for filtering content by creators
 */
const CreatorFilter = ({
                           creators = [],
                           selectedCreatorIds = [],
                           onCreatorSelect,
                           onClearFilters,
                           isLoading = false
                       }) => {

    const [inputValue, setInputValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredCreators, setFilteredCreators] = useState(creators);

    // Filter creators when search term or creators list changes
    useEffect(() => {
        if (!searchTerm) {
            setFilteredCreators(creators);
            return;
        }

        const normalizedSearchTerm = searchTerm.toLowerCase().trim();
        const filtered = creators.filter(creator =>
            creator.name.toLowerCase().includes(normalizedSearchTerm)
        );

        setFilteredCreators(filtered);
    }, [searchTerm, creators]);

    // Debounced search handler to avoid excessive filtering
    const debouncedSearch = useCallback(
        debounce((term) => {
            setSearchTerm(term);
        }, 300),
        []
    );

    // Handle search input changes
    const handleSearchChange = (value) => {
        setInputValue(value);
        debouncedSearch(value);
    };

    // Handle clear filters
    const handleClearFilters = (e) => {
        e.stopPropagation();

        setSearchTerm('');

        if (onClearFilters) {
            onClearFilters();
        }
    };

    // Calculate dropdown placeholder text
    const selectedCount = selectedCreatorIds.length;

    const dropdownPlaceholder = selectedCount
        ? `Filter by Creators (${selectedCount} Selected)`
        : 'Filter by Creators';

    return (
        <div className="creator-filter-container">
            <CustomDropdown
                placeholder={dropdownPlaceholder}
                align="right"
                dropdownClassName="creator-filter-dropdown"
                detailsClassname="creator-filter-dropdown-details"
                wrapperClassName={`creator-filter-dropdown-container ${
                    isLoading ? 'creator-filter-disabled' : ''
                }`}
                tooltip="Filter content by specific creators"
                tooltipPosition="top"
                isToggleOnSelect={false}
                disabled={isLoading}
                asInput
                inputValue={inputValue}
                onInputChange={handleSearchChange}
            >
                <div className="creator-filter-content">
                    <div className="creator-filter-list">
                        {filteredCreators.length > 0 ? (
                            filteredCreators.map(creator => (
                                <CreatorFilterItem
                                    key={creator.id}
                                    creator={creator}
                                    isSelected={selectedCreatorIds.includes(creator.id)}
                                    onSelect={() => onCreatorSelect(creator.id)}
                                />
                            ))
                        ) : (
                            <div className="creator-filter-empty">
                                <Icon
                                    icon="triangle-exclamation"
                                    color="var(--gray-01)"
                                />

                                <span>No creators found</span>
                            </div>
                        )}
                    </div>

                    {selectedCreatorIds.length > 0 && (
                        <div className="creator-filter-footer">
                            <FormButton
                                className="creator-filter-clear-button"
                                onClick={handleClearFilters}
                                type="button"
                                color="blue"
                                textButton
                            >
                                Deselect All
                            </FormButton>
                        </div>
                    )}
                </div>
            </CustomDropdown>
        </div>
    );
};

CreatorFilter.propTypes = {
    creators: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string.isRequired
        })
    ),
    selectedCreatorIds: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
    onCreatorSelect: PropTypes.func.isRequired,
    onClearFilters: PropTypes.func.isRequired,
    isLoading: PropTypes.bool
};

export default CreatorFilter;




// WEBPACK FOOTER //
// ./brand/content_approval/creator_filter/creator_filter.js